/**
 * Accepts Parent/Caller props to return a common UI that then returns a Child UI .
 *
 * @param open Open or close dialogue.
 * @param acceptButtonLabel If present, set accept/right button label.
 * @param closeButtonLabel If present, set close/cancel button label
 * @param handleClose Close the dialogue.
 * @param handleAccept Call relevant service for processing.
 * @param component Return React Element as Child.
 * @param leftButtons Array of buttons for the lower-left side of the dialogue.
 * @param centerButtons Array of buttons for the lower-center side of the dialogue.
 * @param rightButtons Array of buttons for the right-center side of the dialogue.
 *
 */

import { Grid, withStyles } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import { Translation } from 'react-i18next';
import { VerticalAlignment } from 'src/common/enumerations/verticalAlignment';
import BaseButton from '../../base-button';
import EmeraldDialog from '../../emerald-dialog/emerald-dialog';
import dialogueContainerStyles from './interactive-dialogue-container-styles';
import { styles } from './interactive-dialogue/interactive-dialogue-styles';
import { NCRBaseButtonProps } from '../../base-button/base-button';
import { MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';

type Props = IModal;

export const InteractiveDialogueContainer: React.FC<Props> = ({
  closeButtonLabel = 'Cancel',
  acceptButtonLabel = 'Yes',
  leftButtons = [],
  centerButtons = [],
  rightButtons = [],
  ...props
}): JSX.Element => {
  const dialogStyle = makeStyles({
    dialogStyle: {},
  });
  const defaultWidth = 43.99;
  const defaultHeight = 65.1;
  const classes = styles({
    maxWidth: props.dialogWidth ?? defaultWidth,
    height: props.dialogHeight ?? defaultHeight,
    fullScreen: props.fullScreen ?? false,
    minWidth: props.dialogMinWidthFlag ? 62.5 : 0,
  });
  const dialog = dialogStyle();

  return (
    <EmeraldDialog
      data-testid={props.dialogTestIdPrefix || 'dialog'}
      open={props.open}
      fullWidth={true}
      disableBackdropClick={props.disableBackdropClick}
      classes={{
        paper: `${dialog.dialogStyle} ${classes.dialogPaper}`,
      }}
    >
      <Grid container className={classes.dialogContainerMain}>
        {props.component}
      </Grid>
      {!props.hideButtons &&
        (leftButtons.length + centerButtons.length + rightButtons.length > 0 ? (
          <Grid
            container
            wrap='nowrap'
            justifyContent='space-between'
            className={classes.dialogContainerButtonsMain}
          >
            <Grid
              container
              item
              wrap='nowrap'
              justifyContent='flex-start'
              className={classes.dialogContainerButtons}
            >
              {leftButtons.map((btnProps, i) => {
                return (
                  <BaseButton {...btnProps} key={i}>
                    <Translation>{t => t(`${btnProps.children}`)}</Translation>
                  </BaseButton>
                );
              })}
            </Grid>
            <Grid
              container
              item
              wrap='nowrap'
              justifyContent='center'
              className={classes.dialogContainerButtons}
            >
              {centerButtons.map((btnProps, i) => {
                return (
                  <BaseButton {...btnProps} key={i}>
                    <Translation>{t => t(`${btnProps.children}`)}</Translation>
                  </BaseButton>
                );
              })}
            </Grid>
            <Grid
              container
              item
              wrap='nowrap'
              justifyContent='flex-end'
              className={classes.dialogContainerButtons}
            >
              {rightButtons.map((btnProps, i) => {
                return (
                  <BaseButton {...btnProps} key={i}>
                    <Translation>{t => t(`${btnProps.children}`)}</Translation>
                  </BaseButton>
                );
              })}
            </Grid>
          </Grid>
        ) : (
          props.buttonComponent || (
            <Grid container className={classes.dialogContent}>
              <Grid
                item
                xs={12}
                className={
                  props.separateButton
                    ? classes.dialogSeparateButtonContent
                    : classes.dialogItemContent
                }
              >
                {!props.hideCancelButton && (
                  <BaseButton
                    className={classes.closeButton}
                    fullWidth
                    data-testid={
                      props.dialogTestIdPrefix ? `${props.dialogTestIdPrefix}-close` : 'Close'
                    }
                    buttonType='white'
                    onClick={props.handleClose}
                  >
                    <Translation>{t => t(closeButtonLabel)}</Translation>
                  </BaseButton>
                )}
                {!props.hideAcceptButton && (
                  <BaseButton
                    className={classes.acceptButton}
                    data-testid={
                      props.dialogTestIdPrefix ? `${props.dialogTestIdPrefix}-accept` : 'Accept'
                    }
                    onClick={props.handleAccept}
                  >
                    <Translation>{t => t(acceptButtonLabel)}</Translation>
                  </BaseButton>
                )}
              </Grid>
            </Grid>
          )
        ))}
    </EmeraldDialog>
  );
};

export interface IModal extends IDialogueBase {
  modalId?: string;
  size?: MessageModalSizeEnum;
  leftButtons?: NCRBaseButtonProps[];
  rightButtons?: NCRBaseButtonProps[];
  centerButtons?: NCRBaseButtonProps[];
}
interface IDialogueBase {
  dialogTestIdPrefix?: string;
  open: boolean;
  acceptButtonLabel?: string;
  closeButtonLabel?: string;
  hideButtons?: boolean;
  hideCancelButton?: boolean;
  hideAcceptButton?: boolean;
  disableBackdropClick?: boolean;
  handleClose?: () => void;
  handleAccept?: () => void;
  component?: JSX.Element;
  buttonComponent?: JSX.Element;
  separateButton?: boolean;
  verticalAlignment?: VerticalAlignment;
  dialogWidth?: number;
  dialogHeight?: number;
  fullScreen?: boolean;
  dialogMinWidthFlag?: boolean;
}

export default withStyles(dialogueContainerStyles, { name: 'InteractiveDialogueContainer' })(
  InteractiveDialogueContainer,
);
